import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertTriangle, Copy, RotateCcw } from 'lucide-react';

/**
 * Catches render-time crashes so one broken page can't take the whole app down.
 *
 * Without this, any error thrown while rendering unmounts the entire React tree
 * and leaves a blank white page with nothing on screen — the failure is real but
 * completely invisible, and indistinguishable from "the server is down".
 *
 * Note what this does NOT catch, by design of React: errors in event handlers,
 * in async callbacks, or inside `setTimeout`. Failed queries aren't caught
 * either — TanStack Query surfaces those as `isError` and each page renders its
 * own retry state.
 */

interface Props {
    children: ReactNode;
    /** Shown above the message, e.g. "Client Accounts". */
    where?: string;
    /** Keeps the sidebar/topbar usable when only the page content failed. */
    inline?: boolean;
}

interface State {
    error: Error | null;
    componentStack: string;
}

export class ErrorBoundary extends Component<Props, State> {
    override state: State = { error: null, componentStack: '' };

    static getDerivedStateFromError(error: Error): Partial<State> {
        return { error };
    }

    override componentDidCatch(error: Error, info: ErrorInfo): void {
        this.setState({ componentStack: info.componentStack ?? '' });
        // Keep the full detail in the console for anyone with DevTools open.
        console.error('[ErrorBoundary]', this.props.where ?? 'app', error, info.componentStack);
    }

    private reset = () => this.setState({ error: null, componentStack: '' });

    private copy = () => {
        const { error, componentStack } = this.state;
        const text = [
            `Page: ${this.props.where ?? 'app'}`,
            `Error: ${error?.name}: ${error?.message}`,
            '',
            error?.stack ?? '',
            '',
            'Component stack:',
            componentStack,
        ].join('\n');
        void navigator.clipboard?.writeText(text);
    };

    override render() {
        const { error, componentStack } = this.state;
        if (!error) return this.props.children;

        return (
            <div className={this.props.inline ? 'py-8' : 'flex min-h-screen items-center justify-center bg-ink-50 p-6'}>
                <div className="mx-auto w-full max-w-2xl rounded-xl border border-red-200 bg-white p-6">
                    <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50">
                            <AlertTriangle className="h-5 w-5 text-red-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <h2 className="font-display text-lg font-bold text-brand-navy">
                                {this.props.where ? `${this.props.where} hit an error` : 'This page hit an error'}
                            </h2>
                            <p className="mt-1 text-sm text-ink-500">
                                The rest of the app is still running. Copy the details below and send them on —
                                they name the exact line that failed.
                            </p>

                            <div className="mt-4 rounded-lg border border-ink-100 bg-ink-50 p-3">
                                <div className="font-mono text-[13px] font-semibold text-red-700 break-words">
                                    {error.name}: {error.message}
                                </div>
                                {(error.stack || componentStack) && (
                                    <details className="mt-2">
                                        <summary className="cursor-pointer font-ui text-[11px] font-semibold uppercase tracking-wider text-ink-500">
                                            Stack trace
                                        </summary>
                                        <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed text-ink-700">
{error.stack}
{componentStack ? `\nComponent stack:${componentStack}` : ''}
                                        </pre>
                                    </details>
                                )}
                            </div>

                            <div className="mt-4 flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={this.reset}
                                    className="inline-flex items-center gap-1.5 rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:bg-brand-navy-600"
                                >
                                    <RotateCcw className="h-3.5 w-3.5" /> Try again
                                </button>
                                <button
                                    type="button"
                                    onClick={this.copy}
                                    className="inline-flex items-center gap-1.5 rounded-lg border border-ink-100 px-4 py-2 text-sm font-semibold text-brand-navy hover:bg-ink-50"
                                >
                                    <Copy className="h-3.5 w-3.5" /> Copy details
                                </button>
                                <button
                                    type="button"
                                    onClick={() => window.location.reload()}
                                    className="inline-flex items-center gap-1.5 rounded-lg border border-ink-100 px-4 py-2 text-sm font-semibold text-brand-navy hover:bg-ink-50"
                                >
                                    Reload page
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

/**
 * Route-scoped boundary. Re-keying on the pathname clears a caught error as
 * soon as the user navigates elsewhere, so a single bad page doesn't leave the
 * whole shell stuck on the error screen.
 */
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
    const { pathname } = useLocation();
    return (
        <ErrorBoundary key={pathname} where={pathname} inline>
            {children}
        </ErrorBoundary>
    );
}
